import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import { useTheme, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Icon from '@mdi/react';
import { mdiArrowLeftThin } from '@mdi/js';
import { bgGradient } from '@mgm/common/utils';

import { BaseLink } from '@mgm/common/components/base';
import { NavLink } from 'react-router-dom';

const CommissionModelOptions = () => {
  const theme = useTheme();
  const { t } = useTranslation(['commissions', 'inputs', 'team', 'common']);

  const color = theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#FFFFFF';
  return (
    <Box
      sx={{
        ...bgGradient({
          color: alpha(color, 0.9),
          imgUrl: '/static/overlay_1.jpg',
        }),
        ...(theme.palette.mode === 'dark' && {
          color: 'common.white',
        }),
        py: { xs: 10, md: 2 },
      }}
    >
      <BaseLink NavLinkComponent={NavLink} to="/commissions/teams">
        <Box paddingY={2} display="flex" justifyContent="start">
          <Icon className="mr-1" path={mdiArrowLeftThin} size={0.7} />
          {t('backToCommissions')}
        </Box>
      </BaseLink>
      <Container maxWidth={false}>
        <Typography variant="h2" sx={{ textAlign: 'center', mb: 5 }}>
          {t('teamCommissionModels')}
        </Typography>

        <Typography
          sx={{
            mt: 3,
            mx: 'auto',
            opacity: 0.72,
            maxWidth: 600,
            textAlign: 'center',
            mb: { xs: 8, md: 10 },
          }}
        >
          {t('teamCommissionModelsAreDesigned')}
        </Typography>

        <Grid container spacing={4}>
          <Grid size={{
            xs: 12, md: 4,
          }}
          >
            <Box sx={{ px: 2 }}>
              <Typography color="primary" variant="h4" gutterBottom>
                {t('split', { ns: 'inputs' })}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '8px' }}>
                {t('goalsAreMetBased')}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                {t('theCommissionIsDistributed')}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                {t('whenSelectingThisModelsTeams')}
              </Typography>
              <Typography variant="subtitle1" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                {`${t('example', { ns: 'common' })}:`}
              </Typography>
              <Typography variant="body2" component="ul" style={{ marginBottom: '16px', paddingLeft: '16px' }}>
                <li>
                  {t('ifTheTotalCalculated')}
                  {' '}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    400$
                    {' '}
                    {' '}
                  </strong>
                  {t('AndThatTeamHas')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    {`4 ${t('members', { ns: 'team' })?.toLowerCase()}`}
                  </strong>
                  <em>
                    -
                    {' '}
                    {t('eachMemberReceives')}
                    {' '}
                    100$.
                  </em>
                </li>
              </Typography>
            </Box>
          </Grid>

          <Grid size={{
            xs: 12, md: 4,
          }}
          >
            <Box sx={{ px: 2 }}>
              <Typography color="primary" variant="h4" gutterBottom>
                {t('duplicate', { ns: 'inputs' })}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '8px' }}>
                {t('goalsAreMetBased')}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                {t('theCommissionIsAwarded')}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                {t('whenSelectingThisModelsTeamsThatReach')}
              </Typography>
              <Typography variant="subtitle1" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                {`${t('example', { ns: 'common' })}:`}
              </Typography>
              <Typography variant="body2" component="ul" style={{ marginBottom: '16px', paddingLeft: '16px' }}>
                <li>
                  {t('ifTheTotalCalculated')}
                  {' '}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    200$
                    {' '}
                    {' '}
                  </strong>
                  <em>
                    -
                    {' '}
                    {t('eachMemberReceives')}
                    {' '}
                    200$.
                  </em>
                </li>
              </Typography>
            </Box>
          </Grid>

          <Grid size={{
            xs: 12, md: 4,
          }}
          >
            <Box sx={{ px: 2 }}>
              <Typography color="primary" variant="h4" gutterBottom>
                {t('teamSteps')}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '8px' }}>
                {t('goalsAreMetBased')}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                {t('compensationIsCalculated')}
              </Typography>

              <Typography variant="subtitle1" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                {t('howDoesItWork')}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                {t('theCommissionStepsAreMet')}
              </Typography>

              <Typography variant="subtitle1" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                {`${t('example', { ns: 'common' })}:`}
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                {t('letSayWeHaveATeam')}
              </Typography>

              <Typography variant="body2" component="ul" style={{ marginBottom: '16px', paddingLeft: '16px' }}>
                <li>
                  {t('ifTheTeamSoldLess')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    5K$
                  </strong>
                  {' '}
                  -
                  {' '}
                  {t('theyDontGetCommission')}
                  .
                  <em>
                    {' '}
                    (
                    {t('thisStepWouldLookLike')}
                    : 1$ - 4999$,
                    {' '}
                    {' '}
                    {t('flat')}
                    : 0$)
                  </em>
                </li>
                <li>
                  {t('ifTheTeamSoldOver')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    5K$
                  </strong>
                  {' '}
                  -
                  {' '}
                  {t('theyEachGet')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    10%
                    {' '}
                    {t('commission')?.toLowerCase()}
                  </strong>
                  {' '}
                  {t('onTheirSales')}
                  .
                  <em>
                    {' '}
                    (
                    {t('thisStepWouldLookLike')}
                    : 5000$ - 9999$,
                    {' '}
                    {t('percentage').toLowerCase()}
                    : 10)
                  </em>
                </li>
                <li>
                  {t('ifTheTeamSoldOver')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    10K$
                  </strong>
                  {' '}
                  -
                  {' '}
                  {t('theyEachGet')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    20%
                    {' '}
                    {t('commission')?.toLowerCase()}
                  </strong>
                  {' '}
                  {t('onTheirSales')}
                  .
                </li>
              </Typography>

              <Typography variant="subtitle1" style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                {t('letsPutSomeNumbers')}
              </Typography>

              <Typography variant="body2" component="ol" style={{ marginBottom: '16px', paddingLeft: '16px' }}>
                <li>
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    A.
                  </strong>
                  {' '}
                  {t('theTeamSoldFor')}
                  {' '}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    4K$
                  </strong>
                  {' '}
                  (
                  {t('lessThen')}
                  {' '}
                  5K$),
                  {' '}
                  {t('theyEachGet')}
                  {' '}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    0
                    {' '}
                    {t('commission')?.toLowerCase()}
                  </strong>
                  .
                </li>
                <li>
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    B.
                  </strong>
                  {' '}
                  {t('theTeamSoldFor')}
                  {' '}

                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    6K$
                  </strong>
                  {' '}
                  (
                  {t('moreThen')}
                  {' '}
                  5K$
                  {' '}
                  {t('butLessThanTheNext')}
                  {' '}
                  10K$ - 10%
                  {' '}
                  {t('commission')}
                  ):
                  <br />
                  Danny
                  {' '}
                  {t('soldFor')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    3K$
                  </strong>
                  {' '}
                  {t('andWillReceive')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    300$
                  </strong>
                  .
                  <br />
                  Roger
                  {' '}
                  {t('soldFor')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    1K$
                  </strong>
                  {' '}
                  {t('andWillReceive')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    100$
                  </strong>
                  .
                  <br />
                  Dana
                  {' '}
                  {t('soldFor')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    2K$
                  </strong>
                  {' '}
                  {t('andWillReceive')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    200$
                  </strong>
                  .
                </li>
                <li>
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    C.
                  </strong>
                  {' '}
                  {t('theTeamSoldFor')}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    12K$
                  </strong>
                  {' '}
                  (
                  {t('moreThen')}
                  {' '}
                  10K$ - 20%
                  {' '}
                  {t('commission')}
                  ):
                  <br />
                  Danny
                  {' '}
                  {t('soldFor')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    6K$
                  </strong>
                  {' '}
                  {t('andWillReceive')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    1200$
                  </strong>
                  .
                  <br />
                  Roger
                  {' '}
                  {t('soldFor')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    2K$
                  </strong>
                  {' '}
                  {t('andWillReceive')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    400$
                  </strong>
                  .
                  <br />
                  Dana
                  {' '}
                  {t('soldFor')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    4K$
                  </strong>
                  {' '}
                  {t('andWillReceive')}
                  {' '}
                  <strong style={{
                    color: '#A855F7',
                  }}
                  >
                    800$
                  </strong>
                  .
                </li>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export { CommissionModelOptions };
